import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { organizationsAPI } from '../../api/organizations'
import type { Organization } from '../../api/organizations'
import { Button, Card, Alert, PageLayout, PageHeader } from '../ui'

export const OrganizationList: React.FC = () => {
  const intl = useIntl()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const orgs = await organizationsAPI.getMyOrganizations()
      setOrganizations(orgs)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'orgList.loadFailed' })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout>
        <Alert variant="error">{error}</Alert>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={intl.formatMessage({ id: 'orgList.title' })}
        backTo="/dashboard"
        backLabel={intl.formatMessage({ id: 'orgList.backToDashboard' })}
        actions={
          <Button onClick={() => navigate('/organizations/new')}>
            <FormattedMessage id="orgList.create" />
          </Button>
        }
      />

      {organizations.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <p className="text-foreground-muted mb-4"><FormattedMessage id="orgList.empty" /></p>
            <Button onClick={() => navigate('/organizations/new')}>
              <FormattedMessage id="orgList.createFirst" />
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <div
              key={org.id}
              className="cursor-pointer"
              onClick={() => navigate(`/organizations/${org.id}`)}
            >
              <Card elevated className="hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-semibold text-foreground mb-2">{org.name}</h3>
                {org.description && (
                  <p className="text-foreground-muted text-sm mb-4">{org.description}</p>
                )}
                <div className="space-y-2 text-sm text-foreground-muted">
                  <div className="flex justify-between">
                    <span><FormattedMessage id="orgList.members" /></span>
                    <span className="font-medium text-foreground">{org.stats.member_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><FormattedMessage id="orgList.surveys" /></span>
                    <span className="font-medium text-foreground">{org.stats.survey_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><FormattedMessage id="orgList.responses" /></span>
                    <span className="font-medium text-foreground">{org.stats.response_count}</span>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  )
}
